import { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircleIcon, HomeIcon, SparklesIcon } from "@heroicons/react/24/outline";
import ButtonPrimary from "shared/Button/ButtonPrimary";

export interface SubscriptionConfirmationPageProps {
  className?: string;
}

const SubscriptionConfirmationPage: FC<SubscriptionConfirmationPageProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const { name } = useParams();
  const handleExploreLocations = () => {
    navigate("/espace-evenementiel");
  };

  const handleReturnHome = () => {
    navigate("/add-location");
  };

  const renderMain = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col w-full p-6 space-y-8 bg-white border shadow-xl rounded-2xl border-neutral-200 dark:border-neutral-700 dark:bg-neutral-800 xl:p-8"
    >
      {/* Header */}
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        <CheckCircleIcon className="w-16 h-16 mx-auto text-green-500" />
        <h2 className="mt-4 text-2xl font-bold text-neutral-900 dark:text-neutral-100 sm:text-3xl">
          Félicitations ! Votre abonnement est confirmé
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Votre offre <span className="font-bold text-black">{name}</span> est maintenant active.
        </p>
      </motion.div>

      <hr className="border-neutral-200 dark:border-neutral-700" />

      {/* Confirmation Message */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4 text-center"
      >
        <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
          Vous êtes prêt à recevoir vos premières demandes !
        </h3>
        <p className="text-neutral-600 dark:text-neutral-400">
          Votre espace bénéficie désormais d’une visibilité renforcée sur EventToBee.
        </p>
        <p>
          Les visiteurs peuvent vous contacter directement, restez attentif aux demandes reçues dans votre tableau
          de bord
        </p>
      </motion.div>

      {/* Benefits Section */}

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex flex-col gap-4 sm:flex-row sm:justify-center"
      >
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <ButtonPrimary
            onClick={handleReturnHome}
            className="flex items-center justify-center w-full gap-2 bg-gray-600 hover:bg-gray-700 sm:w-auto"
          >
            <HomeIcon className="w-5 h-5" />
            Ajouter un espace
          </ButtonPrimary>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className={`nc-SubscriptionConfirmationPage ${className}`}>
      <main className="container mb-24 mt-11 lg:mb-32">
        <AnimatePresence>
          <motion.div
            key="main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="max-w-4xl mx-auto"
          >
            {renderMain()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default SubscriptionConfirmationPage;
